import { useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';

const FOCUSABLE =
  'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * Portalled to document.body on purpose. A `position: fixed` overlay nested
 * inside an ancestor that has a `transform` is positioned against that
 * ancestor, not the viewport, so it scrolls away with the page. Portalling
 * sidesteps the whole class of bug.
 *
 * It also traps focus. Declaring `aria-modal="true"` without a trap is a lie:
 * a keyboard user tabs straight out of the dialog into the planner behind it,
 * still able to operate a screen the dialog claims to have blocked.
 */
export function Sheet({
  open,
  title,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
}): React.JSX.Element | null {
  const panel = useRef<HTMLDivElement>(null);
  const returnFocusTo = useRef<HTMLElement | null>(null);

  // A layout effect, not a plain effect: focus-on-open has to land in the
  // same commit as the DOM mutation that opened the sheet. A passive effect
  // is scheduled separately and isn't guaranteed to have run by the time
  // whatever triggered this render moves on — under load, a caller that
  // checks focus right after the state update flips can observe the sheet's
  // dialog already in the DOM but not yet focused. That is a real,
  // user-visible flash of misplaced focus for a keyboard/screen-reader user
  // opening the sheet, not only a test-timing artefact.
  useLayoutEffect(() => {
    if (!open) return;

    returnFocusTo.current = document.activeElement as HTMLElement | null;
    panel.current?.focus();

    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = panel.current?.querySelectorAll<HTMLElement>(FOCUSABLE);
      if (!focusable || focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      // Put the caret back where it was, or the page silently loses it.
      returnFocusTo.current?.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="sheet-scrim" onClick={onClose}>
      <div
        className="sheet"
        ref={panel}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sheet-head">
          <h2>{title}</h2>
          <button type="button" className="sheet-close" onClick={onClose}>
            Close
          </button>
        </header>
        <div className="sheet-body">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
